/**
 * Incluir/editar uma entrada do currículo (RN-014). Moldura do modal canônico (Salas).
 * Datas como mês/ano: é como currículo se escreve — dia não interessa.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { colors } from "../../theme/designSystem";
import SavingOverlay from "../SavingOverlay";
import type { CurriculumEntry, CurriculumEntryPayload, CurriculumKind } from "../../services/usersService";

export const CURRICULUM_KINDS: { value: CurriculumKind; label: string; titleLabel: string; institutionLabel: string }[] = [
  { value: "formacao", label: "Formação", titleLabel: "Curso", institutionLabel: "Instituição" },
  { value: "experiencia", label: "Experiência", titleLabel: "Cargo ou função", institutionLabel: "Empresa ou clínica" },
  { value: "certificacao", label: "Curso ou certificação", titleLabel: "Curso ou certificação", institutionLabel: "Instituição" },
  { value: "registro", label: "Registro profissional", titleLabel: "Registro (ex.: CRP 06/123456)", institutionLabel: "Conselho" },
];

const TITLE_MAX = 255;
const INSTITUTION_MAX = 255;
const DESCRIPTION_MAX = 2000;

const fieldSx = {
  backgroundColor: colors.white,
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: colors.border },
    "&:hover fieldset": { borderColor: colors.border },
    "&.Mui-focused fieldset": { borderColor: colors.primary },
  },
};

/** "yyyy-MM-dd" -> "yyyy-MM" (input type=month) e volta com dia 01. */
const toMonthInput = (date: string | null): string => (date ? date.slice(0, 7) : "");
const fromMonthInput = (month: string): string | null => (month ? `${month}-01` : null);

interface Props {
  open: boolean;
  entry: CurriculumEntry | null;
  defaultKind?: CurriculumKind;
  saving: boolean;
  onClose: () => void;
  onSave: (payload: CurriculumEntryPayload) => void;
}

const CurriculumEntryModal: React.FC<Props> = ({ open, entry, defaultKind, saving, onClose, onSave }) => {
  const [kind, setKind] = useState<CurriculumKind>("formacao");
  const [title, setTitle] = useState("");
  const [institution, setInstitution] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [current, setCurrent] = useState(false);
  const [description, setDescription] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!open) return;
    setKind(entry?.kind ?? defaultKind ?? "formacao");
    setTitle(entry?.title ?? "");
    setInstitution(entry?.institution ?? "");
    setStart(toMonthInput(entry?.startedAt ?? null));
    setEnd(toMonthInput(entry?.endedAt ?? null));
    setCurrent(entry?.current ?? false);
    setDescription(entry?.description ?? "");
    setErro("");
  }, [open, entry, defaultKind]);

  const meta = CURRICULUM_KINDS.find((k) => k.value === kind) ?? CURRICULUM_KINDS[0];

  const salvar = () => {
    if (!title.trim()) {
      setErro(`Informe ${meta.titleLabel.toLowerCase()}.`);
      return;
    }
    if (!current && start && end && end < start) {
      setErro("O fim precisa ser igual ou posterior ao início.");
      return;
    }
    setErro("");
    onSave({
      kind,
      title: title.trim(),
      institution: institution.trim() || null,
      startedAt: fromMonthInput(start),
      endedAt: current ? null : fromMonthInput(end),
      current,
      description: description.trim() || null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
    >
      <SavingOverlay open={saving} />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {entry ? "Editar item do currículo" : "Novo item do currículo"}
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{ color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" }, "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" } }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            select
            size="small"
            label="Tipo"
            value={kind}
            onChange={(e) => setKind(e.target.value as CurriculumKind)}
            fullWidth
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
          >
            {CURRICULUM_KINDS.map((k) => (
              <MenuItem key={k.value} value={k.value}>
                {k.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label={meta.titleLabel}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!title.trim()}
            fullWidth
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: TITLE_MAX }}
          />

          <TextField
            size="small"
            label={meta.institutionLabel}
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            fullWidth
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: INSTITUTION_MAX }}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              type="month"
              size="small"
              label="Início"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              fullWidth
              sx={fieldSx}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="month"
              size="small"
              label="Fim"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              disabled={current}
              fullWidth
              sx={fieldSx}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <FormControlLabel
            control={<Checkbox checked={current} onChange={(e) => setCurrent(e.target.checked)} size="small" />}
            label={<Typography sx={{ fontSize: 14 }}>{kind === "experiencia" ? "Trabalho aqui atualmente" : "Em andamento / vigente"}</Typography>}
          />

          <TextField
            size="small"
            label="Descrição (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            // Sem altura fixa no sx: multiline com altura travada faz o texto escapar (gotcha registrado).
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: DESCRIPTION_MAX }}
            helperText={`${description.length}/${DESCRIPTION_MAX}`}
          />

          {erro && <Typography sx={{ fontSize: 13, color: "#dc3545" }}>{erro}</Typography>}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: "1rem 1.5rem", gap: 1, justifyContent: "flex-end", "& .MuiButton-root": { flexShrink: 0, minHeight: 44 } }}
      >
        <Button onClick={onClose} disabled={saving} variant="outlined" sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          onClick={salvar}
          disabled={saving}
          variant="contained"
          sx={{
            backgroundColor: colors.primary,
            textTransform: "none",
            "&.Mui-disabled": { backgroundColor: colors.primary, opacity: 0.7, color: "white" },
          }}
        >
          {saving ? "Processando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CurriculumEntryModal;
